import {
  Alert,
  Card,
  Col,
  DatePicker,
  Empty,
  Grid,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";

import StudentTable from "./StudentTable";
import TrendChart from "./TrendChart";
import { clamp } from "../../../utils/responsiveHelpers";
import {
  abortLatestRequest,
  ANALYTICS_DEFAULT_WINDOW_DAYS,
  ANALYTICS_HIT_RATIO_COLOR,
  ANALYTICS_INFO_TEXT,
  ANALYTICS_RESOLUTION_DISPLAY_CONFIG,
  ANALYTICS_RESOLUTION_ENUM,
  ANALYTICS_RETENTION_DAYS,
  beginLatestRequest,
  EXTERNAL_PROBLEMS_API_BASE,
  REGISTERED_PLATFORM_FILTER_OPTIONS,
  reportUnlessCancelled,
  SHARED_INFO_TEXT,
  formatHitRatioPercent,
  sumColumnWidths,
  toPercentNumber,
  COLUMN_WIDTH_ENUM
} from "../helper";
import InfoHint, { LabelWithHint } from "../InfoHint";

const { Text } = Typography;

// The two cards below sit outside the hit ratio, and each takes its accent
// from the same palette the resolution columns and the trend chart use.
const {
  [ANALYTICS_RESOLUTION_ENUM.INVALID_URL]: { hex: INVALID_URL_HEX },
  [ANALYTICS_RESOLUTION_ENUM.UNSUPPORTED_PLATFORM]: { hex: UNSUPPORTED_PLATFORM_HEX }
} = ANALYTICS_RESOLUTION_DISPLAY_CONFIG;

/**
 * Width of every column the per-platform table declares.
 *
 * antd renders the header and the body as two independent tables and each
 * redistributes the slack on its own, so `scroll.x` has to equal the sum of the
 * declared widths or the two disagree on where a column starts. The resolution
 * columns are generated from `ANALYTICS_RESOLUTION_DISPLAY_CONFIG`, so the total is
 * derived from it rather than written down: adding a resolution would otherwise
 * widen the table and leave the number behind.
 */
const PLATFORM_TABLE_COLUMN_WIDTHS = Object.freeze({
  platform: COLUMN_WIDTH_ENUM.DATE,
  hasCatalogueData: COLUMN_WIDTH_ENUM.SHORT,
  attemptCount: COLUMN_WIDTH_ENUM.SHORT,
  resolution: COLUMN_WIDTH_ENUM.COUNT,
  hitRatio: COLUMN_WIDTH_ENUM.SHORT,
  distinctStudentCount: COLUMN_WIDTH_ENUM.WIDER
});

const { resolution: RESOLUTION_COLUMN_WIDTH, ...SINGLE_COLUMN_WIDTHS } =
  PLATFORM_TABLE_COLUMN_WIDTHS;

const PLATFORM_TABLE_SCROLL_WIDTH =
  sumColumnWidths(SINGLE_COLUMN_WIDTHS) +
  RESOLUTION_COLUMN_WIDTH * Object.keys(ANALYTICS_RESOLUTION_DISPLAY_CONFIG).length;

const OVERVIEW_INITIAL_STATE = Object.freeze({
  totals: null,
  platforms: [],
  queue: null,
  window: null
});

/** The two counters outside the hit ratio, each read from `totals` and tinted from the resolution palette. */
const EXCLUDED_RESOLUTION_CARDS = Object.freeze([
  {
    title: "Invalid URL",
    countField: "invalidUrlCount",
    color: INVALID_URL_HEX,
    description: "Excluded from hit ratio: the link itself was never resolvable."
  },
  {
    title: "Unsupported Platform",
    countField: "unsupportedPlatformCount",
    color: UNSUPPORTED_PLATFORM_HEX,
    description:
      "Excluded from hit ratio: the link pointed at a site the platform list does not cover."
  }
]);

/**
 * Orders two rows by one of their numeric fields, ascending, for an antd
 * column sorter.
 *
 * @param {string} countField - Name of the numeric field on each row.
 * @returns {(first: object, second: object) => number}
 */
const sortByCountField = (countField) => (first, second) => first[countField] - second[countField];

/**
 * The headline cards that show one number beside an explanation, in display order.
 *
 * @param {object} params
 * @param {object} params.totals - Window totals from the overview response.
 * @param {object} params.queue - Queue counters from the overview response.
 * @returns {Array<{title: string, infoText: string, value: number}>}
 */
const buildHeadlineCards = ({ totals, queue }) => {
  const { attemptCount, distinctStudentCount } = totals;
  const { openCount, distinctStudentsWaiting, fulfilledInWindowCount } = queue;

  return [
    {
      title: "Attempts",
      infoText: ANALYTICS_INFO_TEXT.ATTEMPT_COUNT,
      value: attemptCount
    },
    {
      title: "Distinct Students",
      infoText: ANALYTICS_INFO_TEXT.DISTINCT_STUDENT_COUNT,
      value: distinctStudentCount
    },
    {
      title: "Open Requests",
      infoText: ANALYTICS_INFO_TEXT.QUEUE_OPEN,
      value: openCount
    },
    {
      title: "Students Waiting",
      infoText: ANALYTICS_INFO_TEXT.QUEUE_STUDENTS_WAITING,
      value: distinctStudentsWaiting
    },
    {
      title: "Fulfilled In Window",
      infoText: ANALYTICS_INFO_TEXT.QUEUE_FULFILLED,
      value: fulfilledInWindowCount
    }
  ];
};

/**
 * Builds a centred, client-sorted count column of the per-platform table.
 *
 * @param {object} params
 * @param {string} params.countField - Row field holding the count, also the column key.
 * @param {string} params.label
 * @param {string} params.infoText
 * @param {number} params.width
 * @param {Array<string>} params.responsive - antd breakpoints the column shows at.
 * @returns {object} An antd column definition.
 */
const buildSortableCountColumn = ({ countField, label, infoText, width, responsive }) => ({
  title: <LabelWithHint label={label} infoText={infoText} />,
  dataIndex: countField,
  key: countField,
  width,
  align: "center",
  responsive,
  sorter: sortByCountField(countField)
});

const PLATFORM_TABLE_COLUMNS = [
  {
    title: <LabelWithHint label="Platform" infoText={SHARED_INFO_TEXT.PLATFORM} />,
    dataIndex: "platformDisplayName",
    key: "platformDisplayName",
    width: PLATFORM_TABLE_COLUMN_WIDTHS.platform,
    render: (platformDisplayName, { platform }) => (
      <Tag color={platform ? "blue" : "default"}>{platformDisplayName}</Tag>
    )
  },
  {
    title: <LabelWithHint label="Catalogue" infoText={ANALYTICS_INFO_TEXT.CATALOGUE_COLUMN} />,
    dataIndex: "hasCatalogueData",
    key: "hasCatalogueData",
    width: PLATFORM_TABLE_COLUMN_WIDTHS.hasCatalogueData,
    responsive: ["md"],
    render: (hasCatalogueData) => (
      <Tag color={hasCatalogueData ? "green" : "default"}>
        {hasCatalogueData ? "Catalogued" : "Not catalogued"}
      </Tag>
    )
  },
  buildSortableCountColumn({
    countField: "attemptCount",
    label: "Attempts",
    infoText: ANALYTICS_INFO_TEXT.ATTEMPT_COUNT,
    width: PLATFORM_TABLE_COLUMN_WIDTHS.attemptCount,
    responsive: ["sm"]
  }),
  ...Object.entries(ANALYTICS_RESOLUTION_DISPLAY_CONFIG).map(
    ([resolutionKey, { label, color, countField, infoText }]) => ({
      title: <LabelWithHint label={label} infoText={infoText} />,
      dataIndex: countField,
      key: resolutionKey,
      width: RESOLUTION_COLUMN_WIDTH,
      align: "center",
      responsive: ["lg"],
      render: (count) => <Tag color={color}>{count}</Tag>
    })
  ),
  {
    title: <LabelWithHint label="Hit Ratio" infoText={ANALYTICS_INFO_TEXT.HIT_RATIO} />,
    dataIndex: "hitRatio",
    key: "hitRatio",
    width: PLATFORM_TABLE_COLUMN_WIDTHS.hitRatio,
    align: "center",
    responsive: ["sm"],
    render: (hitRatio, { hasCatalogueData, answerableCount }) => (
      <Tooltip
        title={hasCatalogueData ? undefined : "Nothing in the catalogue could have answered this."}
      >
        <Text type={hasCatalogueData ? undefined : "secondary"}>
          {formatHitRatioPercent(hitRatio, hasCatalogueData && answerableCount > 0)}
        </Text>
      </Tooltip>
    )
  },
  buildSortableCountColumn({
    countField: "distinctStudentCount",
    label: "Distinct Students",
    infoText: ANALYTICS_INFO_TEXT.DISTINCT_STUDENT_COUNT,
    width: PLATFORM_TABLE_COLUMN_WIDTHS.distinctStudentCount,
    responsive: ["md"]
  })
];

/**
 * Analytics for the external-problem pipeline: hit ratio, per-platform and
 * per-student breakdowns, and how much the reconciliation queue still owes,
 * all driven by one date-range control and an optional platform filter.
 *
 * Which specific problems are most asked for is the Reconciliation Queue's
 * own question, and it answers it with the whole queue rather than a top ten.
 *
 * @param {object} props
 * @param {string} props.ROOT_URL
 */
const Analytics = ({ ROOT_URL }) => {
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(ANALYTICS_DEFAULT_WINDOW_DAYS, "day").startOf("day"),
    dayjs().endOf("day")
  ]);
  const [platformFilter, setPlatformFilter] = useState(undefined);
  const [overview, setOverview] = useState(OVERVIEW_INITIAL_STATE);
  const [isLoadingOverview, setIsLoadingOverview] = useState(false);
  const [hasLoadedOverview, setHasLoadedOverview] = useState(false);
  const abortControllerRef = useRef(null);
  const screens = Grid.useBreakpoint();

  const isMobileView = screens.md === false;
  const [startDate, endDate] = dateRange;
  const startDateIso = startDate.toISOString();
  const endDateIso = endDate.toISOString();
  const { totals, platforms, queue, window: telemetryWindow } = overview;

  /**
   * Loads the overview for the chosen date range and platform. Only the latest
   * call writes state, and a failure leaves the previous overview on screen.
   */
  const fetchOverview = useCallback(() => {
    const { signal } = beginLatestRequest(abortControllerRef);

    setIsLoadingOverview(true);
    axios
      .get(`${ROOT_URL}/${EXTERNAL_PROBLEMS_API_BASE}/analytics/overview`, {
        signal,
        params: { startDate: startDateIso, endDate: endDateIso, platform: platformFilter }
      })
      .then((response) => {
        setOverview(response.data);
        setHasLoadedOverview(true);
      })
      .catch(reportUnlessCancelled)
      .finally(() => {
        if (!signal.aborted) {
          setIsLoadingOverview(false);
        }
      });
  }, [ROOT_URL, startDateIso, endDateIso, platformFilter]);

  /**
   * Applies the picked date range as whole-day bounds. A cleared picker keeps
   * the current range.
   *
   * @param {Array<import("dayjs").Dayjs>|null} dates - Start and end of the range, null when cleared.
   */
  const handleDateRangeChange = (dates) => {
    if (!dates) {
      return;
    }

    const [rangeStart, rangeEnd] = dates;
    setDateRange([rangeStart.startOf("day"), rangeEnd.endOf("day")]);
  };

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  useEffect(() => () => abortLatestRequest(abortControllerRef), []);

  return (
    <div style={{ padding: clamp(12, 24) }}>
      <Space direction="vertical" size={16} style={{ width: "100%", marginBottom: 24 }}>
        <Space wrap size={12}>
          <DatePicker.RangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            allowClear={false}
            style={{ maxWidth: "100%" }}
          />
          <Select
            placeholder="All platforms"
            allowClear
            style={{ minWidth: 160 }}
            options={REGISTERED_PLATFORM_FILTER_OPTIONS}
            onChange={setPlatformFilter}
          />
          <InfoHint title={ANALYTICS_INFO_TEXT.PLATFORM_FILTER_SCOPE} />
        </Space>
        {telemetryWindow?.isClampedToRetention && (
          <Alert
            type="info"
            showIcon
            message={`Telemetry only retains ${ANALYTICS_RETENTION_DAYS} days of history, so the requested range was shortened to fit.`}
          />
        )}
      </Space>

      {!hasLoadedOverview && (
        <Card style={{ marginBottom: 24 }} loading={isLoadingOverview}>
          <Empty description="The overview could not be loaded." />
        </Card>
      )}

      {totals && queue && (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Card style={{ height: "100%" }}>
                <div style={{ display: "flex", alignItems: "center", gap: clamp(12, 16) }}>
                  <Progress
                    type="circle"
                    percent={toPercentNumber(totals.hitRatio)}
                    strokeColor={ANALYTICS_HIT_RATIO_COLOR}
                    size={72}
                  />
                  <LabelWithHint
                    label={<Text strong>Hit Ratio</Text>}
                    infoText={ANALYTICS_INFO_TEXT.HIT_RATIO}
                  />
                </div>
              </Card>
            </Col>
            {buildHeadlineCards({ totals, queue }).map(({ title, infoText, value }) => (
              <Col key={title} xs={24} sm={12} md={8} lg={4}>
                <Card style={{ height: "100%" }}>
                  <Statistic title={<LabelWithHint label={title} infoText={infoText} />} value={value} />
                </Card>
              </Col>
            ))}
          </Row>

          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            {EXCLUDED_RESOLUTION_CARDS.map(({ title, countField, color, description }) => (
              <Col key={title} xs={24} sm={12}>
                <Card>
                  <Statistic title={title} value={totals[countField]} valueStyle={{ color }} />
                  <Text type="secondary">{description}</Text>
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}

      <TrendChart ROOT_URL={ROOT_URL} startDateIso={startDateIso} endDateIso={endDateIso} />

      <Card
        title={
          <LabelWithHint
            label="Per-Platform Breakdown"
            infoText={ANALYTICS_INFO_TEXT.PLATFORM_BREAKDOWN}
          />
        }
        style={{ marginBottom: 24 }}
      >
        <Table
          loading={isLoadingOverview}
          locale={{
            emptyText:
              hasLoadedOverview && !isLoadingOverview ? (
                <Empty description="No platform activity in this window." />
              ) : (
                <div style={{ minHeight: 120 }} />
              )
          }}
          rowKey={({ platform }, index) => platform || `unrecognised-${index}`}
          columns={PLATFORM_TABLE_COLUMNS}
          dataSource={platforms}
          bordered
          size={isMobileView ? "small" : "middle"}
          pagination={false}
          scroll={{ x: isMobileView ? "max-content" : PLATFORM_TABLE_SCROLL_WIDTH }}
          onRow={({ hasCatalogueData }) => ({
            style: hasCatalogueData ? undefined : { opacity: 0.7 }
          })}
        />
      </Card>

      <StudentTable ROOT_URL={ROOT_URL} startDateIso={startDateIso} endDateIso={endDateIso} />
    </div>
  );
};

export default Analytics;
